import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { submitIntegrationRequest, notifyRequestEvent } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { useEffectiveRequester } from '../requests';
import { useDuplicateDetection } from './useDuplicateDetection.js';

const emptyForm = (requester = '') => ({
  requester,
  department: 'product',
  scope: 'tenant', // 'tenant' | 'org'
  name: '',
  vendor: '',
  url: '',
  business_intent: '',
  action_types: { read: true, write: false, push: false },
  data_sensitivity: { pii: 'no', financial: 'no', compliance: 'no' }, // 'no' | 'maybe' | 'yes'
  priority: 'medium', // 'low' | 'medium' | 'high' | 'critical'
  expected_users: '10',
});

const isValidUrl = (value) => {
  if (!value) return false;
  // Require an explicit scheme — `new URL()` alone accepts single-label hosts
  // (e.g. `https://wdawdw` parses fine), so garbage input needs both checks.
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    return new URL(value).hostname.includes('.');
  } catch {
    return false;
  }
};

/**
 * Request Integration form state (CT-4175) + validation/submit (CT-4176).
 * Requester name is pre-filled from the signed-in user (read-only in the modal).
 * `prefillName` (Marketplace empty state / Provider Detail) seeds the integration name.
 */
export function useRequestIntegrationForm({ prefillName, isOpen } = {}) {
  const { t } = useTranslation('admin-studio');
  // Identity is persona-aware (CT-4203): admin persona → real signed-in user;
  // user persona (demo) → the seed end-user (Danilo Petrov), so a request filed
  // in user view shows up in that user's "My requests". Both the submit here and
  // the owner filter agree because they share useEffectiveRequester.
  const { requesterId, requesterName } = useEffectiveRequester();

  const [form, setForm] = useState(() => emptyForm(requesterName));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(null);
  const submittingRef = useRef(false);

  // Case 2 (already pending) gates submit unless the user opts in (CT-4205).
  const [submitAnyway, setSubmitAnyway] = useState(false);

  // Reset the form on each modal-open transition; seed requester + prefilled
  // name. Adjusting state during render on a tracked prop change is React's
  // recommended alternative to a setState-in-effect (which cascades renders).
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setForm({ ...emptyForm(requesterName), name: prefillName ?? '' });
      setErrors({});
      setSubmitError(null);
      setSubmittedTicket(null);
      setSubmitAnyway(false);
    }
  }

  // Duplicate detection (CT-4205) lives in its own hook; it loads on open and
  // fuzzy-matches the typed name against connected integrations + own requests.
  const { duplicateConnected, duplicatePending, ready: duplicatesReady } = useDuplicateDetection({
    isOpen,
    requesterId,
    name: form.name,
  });

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSensitivity = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, data_sensitivity: { ...prev.data_sensitivity, [key]: value } }));
  }, []);

  const toggleActionType = useCallback((key) => {
    setForm((prev) => ({
      ...prev,
      action_types: { ...prev.action_types, [key]: !prev.action_types[key] },
    }));
  }, []);

  const validateField = useCallback(
    (key) => {
      let message = null;
      if (key === 'name' && !form.name.trim()) message = 'required';
      if (key === 'business_intent' && !form.business_intent.trim()) message = 'required';
      if (key === 'url') {
        if (!form.url.trim()) message = 'required';
        else if (!isValidUrl(form.url.trim())) message = 'url';
      }
      setErrors((prev) => ({ ...prev, [key]: message }));
      return !message;
    },
    [form.name, form.business_intent, form.url],
  );

  const validateAll = useCallback(() => {
    const next = {};
    if (!form.name.trim()) next.name = 'required';
    if (!form.business_intent.trim()) next.business_intent = 'required';
    if (!form.url.trim()) next.url = 'required';
    else if (!isValidUrl(form.url.trim())) next.url = 'url';
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form.name, form.business_intent, form.url]);

  // `!submittedTicket` — once a submission has succeeded, re-submitting would
  // create a duplicate ticket (the fields are still populated with valid data).
  // Reopening the modal resets `submittedTicket` (above), unlocking a new one.
  const canSubmit = useMemo(
    () =>
      !submittedTicket &&
      // Hold submit until the duplicate check has loaded, so a request can't be
      // filed before the Case-2 gate can apply (avoids racing the fetch).
      duplicatesReady &&
      form.name.trim() &&
      form.business_intent.trim() &&
      form.url.trim() &&
      isValidUrl(form.url.trim()) &&
      // Case 2 (already pending) blocks submit unless "Submit anyway" is checked (CT-4205).
      (!duplicatePending || submitAnyway),
    [form.name, form.business_intent, form.url, submittedTicket, duplicatesReady, duplicatePending, submitAnyway],
  );

  const submit = useCallback(async () => {
    if (submittingRef.current || submittedTicket) return; // in flight, or already succeeded
    if (!validateAll()) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        requester: form.requester,
        // requester_id (CT-4202): captured from the signed-in user so My Requests
        // (CT-4203) can filter to "own requests only" once RBAC lands (CT-3900).
        requester_id: requesterId,
        department: form.department,
        scope: form.scope,
        name: form.name.trim(),
        vendor: form.vendor.trim(),
        url: form.url.trim(),
        business_intent: form.business_intent.trim(),
        // The form holds action types as a { read, write, push } boolean map for
        // the UI, but the rest of the system (inbox row, detail panel, seeds)
        // expects an array of the enabled keys — normalize on submit so a
        // submitted request renders like the seeds.
        action_types: Object.keys(form.action_types).filter((key) => form.action_types[key]),
        data_sensitivity: form.data_sensitivity,
        priority: form.priority,
        expected_users: form.expected_users,
      };
      const res = await submitIntegrationRequest(body, new Date().toISOString());
      const normalized = validateApiResponse(res, t('common.requestFailed'));
      const ticket = normalized.body?.ticket_number ?? null;
      setSubmittedTicket(ticket);
      // CT-4204: notify workspace admins of the new submission (payload only —
      // the requester's own confirmation toast is fired by the modal, CT-4177).
      //
      // Best-effort by design: the notification is a secondary side-effect and
      // must NOT be able to turn a successful submission into a failure. Today
      // `notifyRequestEvent` is a V1 stub that always resolves; this `.catch`
      // is the seam that keeps the submit flow intact once TODO(CT-3904) swaps
      // in the real platform notification channel, which can reject at runtime.
      if (ticket) {
        notifyRequestEvent('submitted', ticket, {
          name: body.name,
          requester: form.requester,
        }).catch(() => {});
      }
      // Returned so the caller (modal) can fire the success toast + role route
      // (CT-4177) right at the completion point — no effect on `submittedTicket`.
      return ticket;
    } catch (err) {
      // Keep the form data; surface a retryable error banner.
      setSubmitError(err?.message ?? t('common.requestFailed'));
      return null;
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [form, requesterId, validateAll, t, submittedTicket]);

  return {
    form,
    setField,
    setSensitivity,
    toggleActionType,
    errors,
    submitError,
    setSubmitError,
    isSubmitting,
    submittedTicket,
    submittingRef,
    validateField,
    validateAll,
    submit,
    canSubmit: Boolean(canSubmit),
    // Duplicate detection (CT-4205)
    duplicateConnected,
    duplicatePending,
    submitAnyway,
    setSubmitAnyway,
  };
}
